import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, login, logout, signup } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const openAuthModal = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setShowMobileMenu(false);
    setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
    setFormError('');
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
    setShowMobileMenu(false);
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      if (authMode === 'signup') {
        // Validation
        if (!formData.name || !formData.email || !formData.phone || !formData.password) {
          setFormError(i18n.language === 'uz' ? 'Barcha maydonlarni to\'ldiring' : 'Заполните все поля');
          setIsSubmitting(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setFormError(i18n.language === 'uz' ? 'Parollar mos kelmaydi' : 'Пароли не совпадают');
          setIsSubmitting(false);
          return;
        }
        if (formData.password.length < 6) {
          setFormError(i18n.language === 'uz' ? 'Parol kamida 6 ta belgidan iborat bo\'lishi kerak' : 'Пароль должен содержать минимум 6 символов');
          setIsSubmitting(false);
          return;
        }

        const success = await signup(formData.name, formData.email, formData.phone, formData.password);
        if (success) {
          setShowAuthModal(false);
          setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
        } else {
          setFormError(i18n.language === 'uz' ? 'Bu email allaqachon ro\'yxatdan o\'tgan' : 'Этот email уже зарегистрирован');
        }
      } else {
        // Login
        if (!formData.email || !formData.password) {
          setFormError(i18n.language === 'uz' ? 'Email va parolni kiriting' : 'Введите email и пароль');
          setIsSubmitting(false);
          return;
        }

        const success = await login(formData.email, formData.password);
        if (success) {
          setShowAuthModal(false);
          setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
        } else {
          setFormError(i18n.language === 'uz' ? 'Noto\'g\'ri email yoki parol' : 'Неверный email или пароль');
        }
      }
    } catch (error) {
      setFormError(i18n.language === 'uz' ? 'Xatolik yuz berdi' : 'Произошла ошибка');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServicesClick = () => {
    if (location.pathname === '/') {
      const el = document.getElementById('services');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#services');
      setTimeout(() => {
        const el = document.getElementById('services');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
    setShowMobileMenu(false);
  };

  const handleAboutClick = () => {
    if (location.pathname === '/') {
      const el = document.getElementById('about');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#about');
      setTimeout(() => {
        const el = document.getElementById('about');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
    setShowMobileMenu(false);
  };

  const handleContactClick = () => {
    if (location.pathname === '/') {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#contact');
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
    setShowMobileMenu(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/c10e9e76a30b13d22dd14b5eb889b083/0b5e06925b727ea3647571943e3d63a6.png" 
                alt="MediVoyage" 
                className="h-16 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => navigate('/')}
                className={`text-sm font-medium transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                } ${location.pathname === '/' ? 'text-teal-600' : ''}`}
              >
                {t('nav_home')}
              </button>
              <button
                onClick={handleServicesClick}
                className={`text-sm font-medium transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                {t('nav_services')}
              </button>
              <button
                onClick={() => navigate('/hospitals')}
                className={`text-sm font-medium transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                } ${location.pathname === '/hospitals' ? 'text-teal-600' : ''}`}
              >
                {t('nav_hospitals')}
              </button>
              <button
                onClick={() => navigate('/posts')}
                className={`text-sm font-medium transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                } ${location.pathname === '/posts' ? 'text-teal-600' : ''}`}
              >
                {t('nav_posts')}
              </button>
              <button
                onClick={handleAboutClick}
                className={`text-sm font-medium transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                {t('nav_about')}
              </button>
              <button
                onClick={handleContactClick}
                className={`text-sm font-medium transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
                }`}
              >
                {t('nav_contact')}
              </button>
            </div>

            {/* Right Side Actions */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Language Switcher */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => i18n.changeLanguage('uz')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    i18n.language === 'uz'
                      ? 'bg-teal-600 text-white'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  UZ
                </button>
                <button
                  onClick={() => i18n.changeLanguage('ru')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    i18n.language === 'ru'
                      ? 'bg-teal-600 text-white'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  RU
                </button>
              </div>

              {/* Auth Section */}
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      isScrolled
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <i className="ri-user-line"></i>
                    <span>{user.name}</span>
                    <i className={`ri-arrow-down-s-line transition-transform ${showUserDropdown ? 'rotate-180' : ''}`}></i>
                  </button>
                  
                  {showUserDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs text-gray-500">{t('welcome_user')}</p>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer bg-transparent border-none"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={() => openAuthModal('login')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      isScrolled
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    {t('login')}
                  </button>
                  <button
                    onClick={() => openAuthModal('signup')}
                    className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
                  >
                    {t('signup')}
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2"
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => { navigate('/'); setShowMobileMenu(false); }}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer bg-transparent border-none"
              >
                {t('nav_home')}
              </button>
              <button
                onClick={handleServicesClick}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer bg-transparent border-none"
              >
                {t('nav_services')}
              </button>
              <button
                onClick={() => { navigate('/hospitals'); setShowMobileMenu(false); }}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer bg-transparent border-none"
              >
                {t('nav_hospitals')}
              </button>
              <button
                onClick={() => { navigate('/posts'); setShowMobileMenu(false); }}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer bg-transparent border-none"
              >
                {t('nav_posts')}
              </button>
              <button
                onClick={handleAboutClick}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer bg-transparent border-none"
              >
                {t('nav_about')}
              </button>
              <button
                onClick={handleContactClick}
                className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer bg-transparent border-none"
              >
                {t('nav_contact')}
              </button>
              
              <div className="flex space-x-2 pt-2">
                <button
                  onClick={() => i18n.changeLanguage('uz')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                    i18n.language === 'uz' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  UZ
                </button>
                <button
                  onClick={() => i18n.changeLanguage('ru')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                    i18n.language === 'ru' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  RU
                </button>
              </div>

              {isAuthenticated && user ? (
                <>
                  <div className="pt-2 pb-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500">{t('welcome_user')}</p>
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 whitespace-nowrap"
                  >
                    <i className="ri-logout-box-line mr-2"></i>
                    {t('logout')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => openAuthModal('login')}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                  >
                    {t('login')}
                  </button>
                  <button
                    onClick={() => openAuthModal('signup')}
                    className="w-full px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-md hover:bg-teal-700 whitespace-nowrap"
                  >
                    {t('signup')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {authMode === 'login' ? t('login') : t('signup')}
            </h2>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{formError}</p>
              </div>
            )}

            <form onSubmit={handleAuthSubmit} className="space-y-4">
              {authMode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('full_name')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              {authMode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('phone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('password')}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              {authMode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('confirm_password')}
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {authMode === 'login' && (
                <div className="text-right">
                  <a href="#" className="text-sm text-teal-600 hover:text-teal-700">
                    {t('forgot_password')}
                  </a>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-3 text-sm font-medium bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '...' : (authMode === 'login' ? t('login') : t('signup'))}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
                className="text-sm text-gray-600 hover:text-teal-600"
              >
                {authMode === 'login' ? `${t('signup')}?` : `${t('login')}?`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}